/**
 * 项目悬浮卡。
 *
 * ⭐ P6.64：卡片【固定在光点位置】显示，不再跟随鼠标移动。
 *   锚点 anchor = 光点中心的【视口坐标】；卡片默认落在光点右下方，
 *   靠近屏幕右 / 下边缘时自动翻转到左 / 上方，避免被裁掉。
 *
 * ⚠️ 之前跟随鼠标（pointer）时：鼠标微抖 → 卡片随之移动 → 滑到鼠标下方 →
 *    光点 onMouseLeave → 卡片消失 → 鼠标又「回到」光点 → 无限闪烁。
 *    固定在光点后，鼠标静止/微动都不会改变卡片位置，闪烁根除。
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { levelColor, levelLabel } from './colors';
import { projectStatusLabel } from '../project/api';
import type { MapProject } from './useMapData';

export interface HoverCardProps {
  project: MapProject;
  /** 光点中心的视口坐标（P6.64 起不再随鼠标移动） */
  anchor: { x: number; y: number };
  /**
   * 项目类型中文名（P6.37 由 MapView 传入字典映射结果）。
   *
   * ⚠️ 组件内**不自行发请求**，保持纯展示；缺省时回退渲染原始 `projectType`（code）。
   */
  projectTypeName?: string;
  /** 是否有编辑 / 删除权限（无权限则不渲染对应按钮） */
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?(project: MapProject): void;
  onDelete?(project: MapProject): void;
  /** 供 MapView 做「移入卡片时取消隐藏」的延时处理 */
  onPointerEnter?(): void;
  onPointerLeave?(): void;
}

export default function HoverCard({
  project,
  anchor,
  projectTypeName,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
  onPointerEnter,
  onPointerLeave,
}: HoverCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });

  /* ⭐ P6.64：基于【光点中心】定位，默认右下偏移；越界则翻转到左上。
     卡片 left 相对光点中心 +18px，光点核心半径 ≤ ~5px，不会遮挡光点本身。 */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    let left = anchor.x + 18;
    let top = anchor.y + 14;
    if (left + r.width > window.innerWidth - margin) left = anchor.x - r.width - 18;
    if (top + r.height > window.innerHeight - margin) top = anchor.y - r.height - 14;
    /* 下限钳制：极端情况下（光点贴边）也不让卡片溢出视口左 / 上 */
    left = Math.max(margin, left);
    top = Math.max(margin, top);
    setPos({ left, top });
  }, [anchor.x, anchor.y, project.id]);

  const color = levelColor(project.level);
  const idleOrFrozen = project.level === 'IDLE' || project.level === 'FROZEN';

  return (
    <div
      ref={ref}
      className="map-hovercard on"
      style={{ left: pos.left, top: pos.top }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <div className="hc-t">
        <span className="d" style={{ background: color }} />
        {project.name}
      </div>
      <div className="hc-s">
        {project.provinceName} · {projectTypeName ?? project.projectType}
      </div>
      <div className="hc-g">
        <div className="hc-i">
          <span className="l">健康度</span>
          <span className="v" style={{ color }}>
            {idleOrFrozen ? levelLabel(project.level) : `${project.healthScore.toFixed(1)} · ${levelLabel(project.level)}`}
          </span>
        </div>
        <div className="hc-i">
          <span className="l">整体进度</span>
          <span className="v">{project.progressPct.toFixed(1)}%</span>
        </div>
        {/* ⭐ P6.54：项目状态中文名（有任务即自动转为「进行中」） */}
        <div className="hc-i">
          <span className="l">状态</span>
          <span className="v">{projectStatusLabel(project.status)}</span>
        </div>
        <div className="hc-i">
          <span className="l">任务</span>
          <span className="v">{project.taskCount} 个</span>
        </div>
        <div className="hc-i">
          <span className="l">逾期</span>
          <span className="v" style={{ color: project.overdueTaskCnt > 0 ? 'var(--alert)' : 'var(--ok)' }}>
            {project.overdueTaskCnt} 个
          </span>
        </div>
        <div className="hc-i">
          <span className="l">指标达标率</span>
          <span className="v">{project.metricPassRate === null ? '—' : `${(project.metricPassRate * 100).toFixed(1)}%`}</span>
        </div>
      </div>

      {canEdit || canDelete ? (
        <div className="hc-actions">
          {canEdit ? (
            <button
              type="button"
              className="hc-act"
              data-action="edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(project);
              }}
            >
              编辑
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="hc-act is-danger"
              data-action="delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(project);
              }}
            >
              删除
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
